function stopTracks(stream) {
  // Helper function that stops all tracks in a stream
  stream?.getTracks().forEach((track) => {
    track.stop();
  });
}

class StreamHandler {
  constructor() {
    this._videoStream = null;
    this._audioStream = null;
  }

  close() {
    if (this._videoStream) this.stopStream(this._videoStream);
    if (this._audioStream) this.stopStream(this._audioStream);
    this._videoStream = null;
    this._audioStream = null;
  }

  async getDevices() {
    return await navigator.mediaDevices.enumerateDevices();
  }

  async permissionsRequested(attempt = 0) {
    const devices = await this.getDevices();
    const noLabels = devices.every((d) => !d.label);
    if (noLabels && attempt < 5) {
      return new Promise((resolve) =>
        setTimeout(() => {
          resolve(this.permissionsRequested(attempt + 1));
        }, 100)
      );
    } else {
      return devices.every((d) => !!d.label || d.deviceId === 'default');
    }
  }

  async verifyPermission({ useAudio = true, useVideo = true }) {
    const stream = await this._requestStream({ useAudio, useVideo });
    if (stream) {
      stopTracks(stream);
      return true;
    }
    return false;
  }

  async _requestStream({ useAudio, useVideo }) {
    // Simplified version of utils/media:getUserMedia() that does not
    // require a socket connection to resolve device object.
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: useAudio,
      video: useVideo,
    });

    return stream;
  }

  async getVideoStream(deviceId) {
    if (this._videoStream) this.stopStream(this._videoStream);
    try {
      const stream = await this._requestStream({
        useAudio: false,
        useVideo: {
          deviceId: deviceId ? { exact: deviceId } : undefined,
        },
      });
      this._videoStream = stream;
      return stream;
    } catch {
      return null;
    }
  }

  async getAudioStream(deviceId) {
    if (this._audioStream) this.stopStream(this._audioStream);
    try {
      const stream = await this._requestStream({
        useAudio: { deviceId: deviceId ? { exact: deviceId } : undefined },
        useVideo: false,
      });
      this._audioStream = stream;
      return stream;
    } catch {
      return null;
    }
  }

  stopStream(stream) {
    stopTracks(stream);
  }
}

export default StreamHandler;
